import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from 'antd';
import { TablePaginationConfig } from 'antd/es/table';
import { ColumnProps } from 'antd/lib/table';
import { SorterResult } from 'antd/lib/table/interface';

import { sortStringAscending } from '@totem/components/services/threats/eventsContainer_legacy/utilities';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import OrganizationContext from '@totem/components/userContext/organizationContext/organizationContext';
import { VulnerabilityRelationshipReportItem } from '@totem/components/vulnerabilities/overviewSummary/types';
import {
  getProbabilityStyle,
  getScoreStyle,
} from '@totem/components/vulnerabilities/report/utilities';
import VulnerabilityReportContext from '@totem/components/vulnerabilities/report/vulnerabilityReportContext';
import { isNotNull } from '@totem/utilities/common';
import { stringifyArray } from '@totem/utilities/tableFilterUtilities';
import { sortNumberAscending } from '@totem/utilities/tableUtilities';

const VulnerabilityReportTable = () => {
  const { data, input, setInput, loading } = useContext(
    VulnerabilityReportContext,
  );
  const { regions, buildings } = useContext(OrganizationContext);

  const columns: ColumnProps<VulnerabilityRelationshipReportItem>[] = [
    {
      title: 'Name',
      dataIndex: 'owner.name',
      key: 'ownerId',
      render: (_, record: VulnerabilityRelationshipReportItem) => (
        <Link to={`/dashboard/devices/${record.owner.id}`}>
          {record.owner.name}
        </Link>
      ),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.owner.name, compB.owner.name),
    },
    {
      title: 'CVE',
      dataIndex: 'sources.vulnerabilities.id',
      key: 'id',
      render: (_, record: VulnerabilityRelationshipReportItem) => (
        <Link
          to={`/dashboard/software/cve/${record.sources.vulnerabilities.id}`}
        >
          {record.sources.vulnerabilities.id}
        </Link>
      ),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(
          compA.sources.vulnerabilities.id,
          compB.sources.vulnerabilities.id,
        ),
    },
    {
      title: 'Probability',
      dataIndex: 'sources.vulnerabilities.probability',
      key: 'probability',
      render: (_, record: VulnerabilityRelationshipReportItem) => (
        <span
          style={getProbabilityStyle(
            record.sources.vulnerabilities.probability,
          )}
        >
          {record.sources.vulnerabilities.probability}
        </span>
      ),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(
          compA.sources.vulnerabilities.probability,
          compB.sources.vulnerabilities.probability,
        ),
      filterMultiple: true,
      filteredValue: stringifyArray(input.probability),
      filters: [
        {
          text: 'Confirmed',
          value: 'Confirmed',
        },
        {
          text: 'Likely',
          value: 'Likely',
        },
        {
          text: 'Possible',
          value: 'Possible',
        },
      ],
    },
    {
      title: 'Likelihood',
      dataIndex: 'sources.vulnerabilities.likelihood',
      key: 'likelihood',
      render: (_, record: VulnerabilityRelationshipReportItem) => (
        <span
          style={getProbabilityStyle(
            record.sources.vulnerabilities.probability,
          )}
        >
          {record.sources.vulnerabilities.likelihood}
        </span>
      ),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortNumberAscending(
          compA.sources.vulnerabilities.likelihood,
          compB.sources.vulnerabilities.likelihood,
        ),
    },
    {
      title: (
        <Tooltip
          title={
            'Score in the context of operational technology (OT) and industrial control systems (ICS), which often have different risk profiles, attack surfaces, and asset criticalities.'
          }
        >
          Contextual Score
        </Tooltip>
      ),
      dataIndex: 'sources.vulnerabilities.score',
      key: 'score',
      render: (_, record: VulnerabilityRelationshipReportItem) => (
        <span style={getScoreStyle(record.sources.vulnerabilities.score)}>
          {record.sources.vulnerabilities.score}
        </span>
      ),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortNumberAscending(
          compA.sources.vulnerabilities.score,
          compB.sources.vulnerabilities.score,
        ),
    },
    {
      title: 'Region',
      dataIndex: 'region.name',
      key: 'regionId',
      render: (_, record: VulnerabilityRelationshipReportItem) =>
        record.region.name,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.region.name, compB.region.name),
      filterMultiple: true,
      filteredValue: stringifyArray(input.regionId),
      filters: isNotNull(regions)
        ? regions.map((chk) => {
            return {
              text: chk.name,
              value: chk.id,
            };
          })
        : [],
    },
    {
      title: 'Building',
      dataIndex: 'building.name',
      key: 'buildingId',
      render: (_, record: VulnerabilityRelationshipReportItem) =>
        record.building.name,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.building.name, compB.building.name),
      filterMultiple: true,
      filteredValue: stringifyArray(input.buildingId),
      filters: isNotNull(buildings)
        ? buildings.map((chk) => {
            return {
              text: chk.name,
              value: chk.id,
            };
          })
        : [],
    },
    {
      title: 'Control System',
      dataIndex: 'controlSystem.name',
      key: 'controlSystemId',
      render: (_, record: VulnerabilityRelationshipReportItem) =>
        record.controlSystem.name,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.controlSystem.name, compB.controlSystem.name),
    },
  ];

  const records = isNotNull(data) && isNotNull(data.data) ? data.data : [];

  const handleTableChange = (
    updatedPagination: TablePaginationConfig,
    filters: SorterResult<VulnerabilityRelationshipReportItem>,
    sorter,
  ) => {
    const { ...params } = filters;

    let sortDir: string = sorter.order === 'descend' ? '-1' : '1';
    if (typeof sorter.order === 'undefined' || sorter.order === null) {
      if (
        typeof input.sortDirection !== 'undefined' &&
        input.sortDirection !== null
      ) {
        sortDir = input.sortDirection;
      }
    }

    let sortField = input.sortField
      ? input.sortField
      : 'sources.vulnerabilities.probability';
    if (
      typeof sorter.field !== 'undefined' &&
      typeof sorter.order !== 'undefined'
    ) {
      sortField = sorter.field;
    }

    // @ts-ignore
    setInput({
      ...input,
      ...params,
      pageSize: updatedPagination.pageSize,
      page: updatedPagination.current,
      sortField,
      sortDirection: sortDir,
    });
  };

  return (
    <IBTable
      showSorterTooltip
      columns={columns}
      dataSource={records}
      loading={loading}
      onChange={handleTableChange}
      pagination={{
        current: input.page,
        pageSize: input.pageSize,
        total:
          isNotNull(data) && isNotNull(data.paging)
            ? data.paging.totalRecords
            : 0,
        showSizeChanger: true,
      }}
      rowKey={(record, idx) =>
        `${record.id}-${record.sources.source.value}-${record.sources.vulnerabilities.id}-${idx}`
      }
    />
  );
};

export default VulnerabilityReportTable;
